import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, OnDestroy} from '@angular/core'; // Removed AfterViewChecked, Added OnDestroy
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {TranslocoService} from "@ngneat/transloco";
import {ToastrService} from "ngx-toastr";
import {finalize, Subject, takeUntil} from 'rxjs'; // Added Subject, takeUntil for subscription management

// Simplified message interface
interface ChatMessage {
    role: 'customer' | 'assistant';
    body: string;
    time: Date;
}

@Component({
    selector: 'app-chat-bot',
    templateUrl: './chat-bot.html',
    styleUrls: ['./chat-bot.css'],
    standalone: false,
})
// Removed AfterViewChecked implementation
export class ChatBot implements OnInit, OnDestroy {

    @ViewChild('chatContainer') private chatContainer: ElementRef<HTMLDivElement> | undefined;
    @ViewChild('txtMain') private messageInput: ElementRef<HTMLInputElement> | undefined;

    // --- Configuration ---
    readonly chatApiUrl = 'https://secure.digitalsignage.com/chatLocal';

    // --- State ---
    m_conversations: ChatMessage[] = [];
    m_messageModel = '';
    isLoading = false;
    errorOccurred = false;

    // --- Helpers ---
    // Removed shouldScrollToBottom flag
    private destroy$ = new Subject<void>(); // For unsubscribing

    constructor(
        private _http: HttpClient,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        this.addInitialMessage('assistant', 'Hello! How can I help you today?');
        this.focusInput();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    sendMessage(): void {
        const userMessageText = this.m_messageModel.trim();
        if (!userMessageText || this.isLoading) {
            return;
        }

        // 1. Add user message & Scroll
        this.addMessageAndScroll('customer', userMessageText);

        const messageToSend = userMessageText; // Store before clearing
        this.m_messageModel = ''; // Clear input
        this.focusInput();

        // 2. Set loading state & Make request
        this.isLoading = true;
        this.errorOccurred = false;
        this.cdr.detectChanges(); // Update UI for loader

        this._http.post<{ response: string }>(this.chatApiUrl, {message: messageToSend})
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges(); // Update UI to remove loader
                    this.focusInput();
                }),
                takeUntil(this.destroy$) // Auto-unsubscribe on component destroy
            )
            .subscribe({
                next: (response) => {
                    const assistantMessage = response?.response?.trim();
                    if (assistantMessage) {
                        // 3a. Add assistant message & Scroll
                        this.addMessageAndScroll('assistant', assistantMessage);
                    } else {
                        console.error("Received empty or invalid response structure:", response);
                        const errorMsgKey = 'error.apiInvalidResponse';
                        const defaultErrorMsg = 'Received an unexpected response from the server.';
                        const errorMsg = this.translocoService.translate(errorMsgKey, {}, defaultErrorMsg);
                        // 3b. Add error message & Scroll
                        this.addMessageAndScroll('assistant', errorMsg);
                        this.errorOccurred = true;
                    }
                },
                error: (error: HttpErrorResponse) => {
                    console.error('Error sending message:', error);
                    this.errorOccurred = true;
                    const errorMsgKey = 'error.apiCommunication';
                    const defaultErrorMsg = 'Sorry, there was an error communicating. Please try again.';
                    const errorMsg = this.translocoService.translate(errorMsgKey, {}, defaultErrorMsg);
                    this.toastr.error(`${errorMsg} (${error.status})`);
                    // 3c. Add error message & Scroll
                    this.addMessageAndScroll('assistant', errorMsg);
                }
            });
    }

    private linkifyUrls(text: string): string {
        const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])|(\bwww\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
        return text.replace(urlRegex, (url) => {
            const fullUrl = url.startsWith('www.') ? `http://${url}` : url;
            // rel="noopener noreferrer" is a security measure for target="_blank"
            return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        });
    }

    private addMessageAndScroll(role: 'customer' | 'assistant', body: string): void {
        let processedBody = body; // Use a temporary variable for the body
        if (role === 'assistant') {
            processedBody = this.linkifyUrls(body);
        }
        this.m_conversations.push({
            role,
            body: processedBody, // Use the processed body
            time: new Date()
        });
        this.cdr.detectChanges();
        this.triggerScrollToBottom();
    }

    private addInitialMessage(role: 'customer' | 'assistant', body: string): void {
        this.m_conversations.push({
            role, body, time: new Date()
        });
        this.cdr.detectChanges();
        this.triggerScrollToBottom();
    }

    // Renamed to avoid confusion with the actual scroll implementation
    private triggerScrollToBottom(): void {
        setTimeout(() => this.scrollToBottom(), 100);
    }

    private scrollToBottom(): void {
        try {
            if (this.chatContainer?.nativeElement) {
                const element = this.chatContainer.nativeElement;
                element.scrollTop = element.scrollHeight;
                // console.log(`Scrolled bottom. Height: ${element.scrollHeight}`);
            }
        } catch (err) {
            console.error("Could not scroll to bottom:", err);
        }
    }

    focusInput(): void {
        // Using setTimeout ensures focus happens after potential DOM updates/reflows
        setTimeout(() => {
            this.messageInput?.nativeElement.focus();
        }, 0);
    }

    clearChat(): void {
        this.m_conversations = [];
        // Add the clearing message using the combined method to ensure scroll
        this.addMessageAndScroll('assistant', 'Chat cleared. How can I help you?');
        this.focusInput();
    }

    downloadChat() {
        let data = '';
        this.m_conversations.forEach((v: ChatMessage) => {
            const timeStr = v.time.toLocaleString();
            const roleStr = v.role === 'customer' ? 'You' : 'Assistant';
            const bodyText = this.stripHtml(v.body);
            data += `[${timeStr}] ${roleStr}: ${bodyText}\n\n`;
        });

        const blob = new Blob([data], {type: 'text/plain;charset=utf-8'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'chat_transcript.txt';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    private stripHtml(html: string): string {
        try {
            const tmp = document.createElement("DIV");
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || "";
        } catch (e) {
            return html; // Fallback
        }
    }
}
